import {
  ADD_TO_CART,
  CLEAR_CART,
  COUNT_CART_TOTALS,
  REMOVE_CART_ITEM,
  TOGGLE_CART_ITEM_AMOUNT,
  UPDATE_CART_VARIANT,
} from '../actions';

const cart_reducer = (state, action) => {
  if (action.type === ADD_TO_CART) {
    const { id, color, size, amount, product, variantSelections } = action.payload;

    // Build unique key from variant selections (or fall back to color+size)
    const variantKey = variantSelections && Object.keys(variantSelections).length > 0
      ? Object.entries(variantSelections)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([k, v]) => `${k}:${typeof v === 'object' ? v.name || v.hex : v}`)
          .join('|')
      : (color || '') + (size || '');

    const itemId = id + variantKey;
    const tempItem = state.cart.find((item) => item.id === itemId);
    if (tempItem) {
      const tempCart = state.cart.map((cartItem) => {
        if (cartItem.id === itemId) {
          let newAmount = cartItem.amount + amount;
          if (newAmount > cartItem.max) {
            newAmount = cartItem.max;
          }
          return { ...cartItem, amount: newAmount };
        } else {
          return cartItem;
        }
      });
      return { ...state, cart: tempCart };
    } else {
      const newItem = {
        id: itemId,
        productId: id,
        name: product.name,
        color,
        size,
        variantSelections: variantSelections || {},
        availableVariants: product.variants || [],
        amount,
        image: product.image || product.images?.[0]?.url || product.images?.[0] || '',
        price: product.price,
        shipping: product.shipping,
        max: product.stock,
      };
      return { ...state, cart: [...state.cart, newItem] };
    }
  }

  if (action.type === UPDATE_CART_VARIANT) {
    const { oldItemId, variantName, newValue, newVariantSelections } = action.payload;
    const item = state.cart.find((i) => i.id === oldItemId);
    if (!item) return state;

    const updatedSelections = newVariantSelections || { ...item.variantSelections, [variantName]: newValue };

    // Build new unique key
    const newVariantKey = Object.entries(updatedSelections)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}:${typeof v === 'object' ? v.name || v.hex : v}`)
      .join('|');
    const newItemId = item.productId + newVariantKey;

    // If an item with the same variant combo already exists, merge amounts
    const existingItem = state.cart.find((i) => i.id === newItemId && i.id !== oldItemId);
    if (existingItem) {
      const mergedAmount = Math.min(existingItem.amount + item.amount, existingItem.max);
      const tempCart = state.cart
        .filter((i) => i.id !== oldItemId)
        .map((i) => i.id === newItemId ? { ...i, amount: mergedAmount } : i);
      return { ...state, cart: tempCart };
    }

    // Extract new color/size strings for backward compat
    const getDisplay = (v) => typeof v === 'object' && v !== null ? (v.name || v.hex || String(v)) : (v || '');
    const colorKey = Object.keys(updatedSelections).find(k => /couleur|color|لون/i.test(k));
    const sizeKey = Object.keys(updatedSelections).find(k => /taille|size|مقاس/i.test(k));

    const tempCart = state.cart.map((i) => {
      if (i.id === oldItemId) {
        return {
          ...i,
          id: newItemId,
          variantSelections: updatedSelections,
          color: colorKey ? getDisplay(updatedSelections[colorKey]) : i.color,
          size: sizeKey ? getDisplay(updatedSelections[sizeKey]) : i.size,
        };
      }
      return i;
    });
    return { ...state, cart: tempCart };
  }

  if (action.type === REMOVE_CART_ITEM) {
    const tempCart = state.cart.filter((item) => item.id !== action.payload);
    return { ...state, cart: tempCart };
  }

  if (action.type === CLEAR_CART) {
    return { ...state, cart: [] };
  }

  if (action.type === TOGGLE_CART_ITEM_AMOUNT) {
    const { id, value } = action.payload;
    const tempCart = state.cart.map((item) => {
      if (item.id === id) {
        //increase
        if (value === 'inc') {
          let newAmount = item.amount + 1;
          if (newAmount > item.max) {
            newAmount = item.max;
          }
          return { ...item, amount: newAmount };
        }
        //decrease
        if (value === 'dec') {
          let newAmount = item.amount - 1;
          if (newAmount < 1) {
            newAmount = 1;
          }
          return { ...item, amount: newAmount };
        }
      }
      return item;
    });
    return { ...state, cart: tempCart };
  }

  if (action.type === COUNT_CART_TOTALS) {
    const { total_items, total_amount, maxShippingFee } = state.cart.reduce(
      (total, cartItem) => {
        const { price, amount, shipping } = cartItem;
        total.total_items += amount;
        total.total_amount += price * amount;
        // Track the highest shipping fee across all cart items
        const productShippingFee = typeof shipping === 'number' ? shipping : (shipping ? 8 : 0);
        if (productShippingFee > total.maxShippingFee) {
          total.maxShippingFee = productShippingFee;
        }
        return total;
      },
      {
        total_items: 0,
        total_amount: 0,
        maxShippingFee: 0,
      }
    );
    
    // Use the product's shipping cost; free shipping for orders >= 200 TND
    let shipping_fee = 0;
    if (maxShippingFee > 0) {
      shipping_fee = total_amount >= 200 ? 0 : maxShippingFee;
    }
    
    return { ...state, total_items, total_amount, shipping_fee };
  }

  throw new Error(`No Matching "${action.type}" - action type`);
};

export default cart_reducer;
